import React, { useState, useEffect, useRef, useCallback } from 'react';
import './TTSButton.css';

// Map Google Translate language codes → speechSynthesis BCP-47 codes
var LANG_MAP = {
  'en':'en-US','hi':'hi-IN','ta':'ta-IN','te':'te-IN','kn':'kn-IN',
  'ml':'ml-IN','mr':'mr-IN','bn':'bn-IN','gu':'gu-IN','pa':'pa-IN',
  'ur':'ur-PK','ar':'ar-SA','zh-CN':'zh-CN','zh-TW':'zh-TW',
  'ja':'ja-JP','ko':'ko-KR','fr':'fr-FR','de':'de-DE','es':'es-ES',
  'pt':'pt-BR','it':'it-IT','ru':'ru-RU','th':'th-TH','vi':'vi-VN',
  'id':'id-ID','ms':'ms-MY','tr':'tr-TR','nl':'nl-NL','pl':'pl-PL',
  'sv':'sv-SE','da':'da-DK','no':'nb-NO','fi':'fi-FI','el':'el-GR',
  'he':'he-IL','ro':'ro-RO','hu':'hu-HU','cs':'cs-CZ','sk':'sk-SK',
  'uk':'uk-UA','fa':'fa-IR','sw':'sw-KE','ne':'ne-NP','si':'si-LK',
  'my':'my-MM','km':'km-KH','fil':'fil-PH','af':'af-ZA',
  'bg':'bg-BG','hr':'hr-HR','sr':'sr-RS','am':'am-ET','zu':'zu-ZA',
  'yo':'yo-NG','ha':'ha-NG','ig':'ig-NG','rw':'rw-RW','so':'so-SO',
  'lo':'lo-LA','sq':'sq-AL'
};

var LANG_NAMES = {
  'en':'English','hi':'Hindi','ta':'Tamil','te':'Telugu','kn':'Kannada',
  'ml':'Malayalam','mr':'Marathi','bn':'Bengali','gu':'Gujarati','pa':'Punjabi',
  'ur':'Urdu','ar':'Arabic','zh-CN':'Chinese (Simplified)','zh-TW':'Chinese (Traditional)',
  'ja':'Japanese','ko':'Korean','fr':'French','de':'German','es':'Spanish',
  'pt':'Portuguese','it':'Italian','ru':'Russian','th':'Thai','vi':'Vietnamese',
  'id':'Indonesian','ms':'Malay','tr':'Turkish','nl':'Dutch','pl':'Polish',
  'sv':'Swedish','da':'Danish','no':'Norwegian','fi':'Finnish','el':'Greek',
  'he':'Hebrew','ro':'Romanian','hu':'Hungarian','cs':'Czech','sk':'Slovak',
  'uk':'Ukrainian','fa':'Persian','sw':'Swahili','ne':'Nepali','si':'Sinhala',
  'my':'Burmese','km':'Khmer','fil':'Filipino','af':'Afrikaans',
  'bg':'Bulgarian','hr':'Croatian','sr':'Serbian','am':'Amharic','zu':'Zulu',
  'yo':'Yoruba','ha':'Hausa','ig':'Igbo','rw':'Kinyarwanda','so':'Somali',
  'lo':'Lao','sq':'Albanian'
};

function getPageLang() {
  var m = document.cookie.match(/googtrans=\/en\/([^;]+)/);
  if (m) return m[1];
  var htmlLang = document.documentElement.lang;
  if (htmlLang && htmlLang !== 'en') return htmlLang;
  return 'en';
}

function detectOS() {
  var ua = navigator.userAgent || '';
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'mac';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Linux/i.test(ua)) return 'linux';
  return 'other';
}

function getSlideText() {
  var slideEl = document.querySelector('[class$="__content"]');
  if (!slideEl) {
    var slides = document.querySelectorAll('[class^="s1"], [class^="s2"], .hero, .about, .vis, .v4, .h5, .s6, .p7, .i8, .d9, .x6');
    if (slides.length > 0) slideEl = slides[slides.length - 1];
  }
  if (!slideEl) return '';

  var texts = [];
  var walker = document.createTreeWalker(slideEl, NodeFilter.SHOW_TEXT, null, false);
  while (walker.nextNode()) {
    var node = walker.currentNode;
    var parent = node.parentElement;
    if (!parent) continue;
    var style = window.getComputedStyle(parent);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') continue;
    var fontSize = parseFloat(style.fontSize);
    if (fontSize < 8) continue;
    var tag = parent.tagName.toLowerCase();
    if (tag === 'script' || tag === 'style' || tag === 'canvas') continue;
    var txt = node.textContent.trim();
    if (txt.length > 0 && txt !== '/' && txt !== '•') texts.push(txt);
  }
  return texts.join('. ').replace(/\.\s*\./g, '.').replace(/\s+/g, ' ');
}

function findExactVoice(voices, langCode) {
  if (!voices || !voices.length) return null;
  var lc = langCode.toLowerCase();
  return voices.find(function(v){
    var vl = (v.lang || '').toLowerCase();
    return vl === lc || vl.indexOf(lc + '-') === 0 || vl.indexOf(lc + '_') === 0;
  }) || null;
}

function chunkText(text, maxLen) {
  var chunks = [];
  var s = text.trim();
  while (s.length > maxLen) {
    var cut = s.lastIndexOf('. ', maxLen);
    if (cut < maxLen * 0.5) cut = s.lastIndexOf(' ', maxLen);
    if (cut < 1) cut = maxLen;
    chunks.push(s.slice(0, cut + 1).trim());
    s = s.slice(cut + 1).trim();
  }
  if (s.length) chunks.push(s);
  return chunks;
}

function loadVoices(timeoutMs) {
  return new Promise(function(resolve){
    if (!window.speechSynthesis) { resolve([]); return; }
    var vs = window.speechSynthesis.getVoices();
    if (vs && vs.length) { resolve(vs); return; }
    var timer = setTimeout(function(){ resolve(window.speechSynthesis.getVoices() || []); }, timeoutMs || 1500);
    window.speechSynthesis.onvoiceschanged = function(){
      var v = window.speechSynthesis.getVoices();
      if (v && v.length) { clearTimeout(timer); resolve(v); }
    };
  });
}

// OS-specific install steps
function getInstallSteps(os, langName) {
  if (os === 'windows') {
    return [
      'Open Windows Settings (press Win + I)',
      'Go to Time & Language → Language & Region',
      'Click Add a language and choose ' + langName,
      'In the install dialog, tick "Text-to-speech" before pressing Install',
      'After install, close the browser completely and reopen it',
      'Open this page again, switch back to ' + langName + ', and tap Listen'
    ];
  }
  if (os === 'mac') {
    return [
      'Open System Settings → Accessibility → Spoken Content',
      'Click the System Voice dropdown → Manage Voices…',
      'Find ' + langName + ' in the list and tick it to download',
      'Wait for the download to finish (~50–200 MB)',
      'Restart the browser, return to this page, and tap Listen'
    ];
  }
  if (os === 'android') {
    return [
      'Open phone Settings → Accessibility → Text-to-speech output',
      'Tap Google Text-to-speech Engine (or the gear next to it)',
      'Tap Install voice data and pick ' + langName,
      'Wait for download to finish',
      'Reopen the browser and tap Listen'
    ];
  }
  if (os === 'ios') {
    return [
      'Open Settings → Accessibility → Spoken Content',
      'Tap Voices and select ' + langName,
      'Tap the voice to download it',
      'Reopen the browser and tap Listen'
    ];
  }
  if (os === 'linux') {
    return [
      'Linux speech voices depend on your distribution',
      'Install speech-dispatcher and the espeak-ng / festival voice for ' + langName,
      'Restart the browser and tap Listen'
    ];
  }
  return [
    'Add ' + langName + ' as a system language with Text-to-Speech enabled',
    'Restart the browser and tap Listen again'
  ];
}

export default function TTSButton({ currentSlide }) {
  var [speaking, setSpeaking] = useState(false);
  var [supported, setSupported] = useState(true);
  var [installModal, setInstallModal] = useState(null); // {lang, langName, os}
  var [showSteps, setShowSteps] = useState(false);
  var sessionRef = useRef({ cancelled: true });

  useEffect(function(){
    if (!window.speechSynthesis) setSupported(false);
  }, []);

  useEffect(function(){
    loadVoices(1500);
  }, []);

  var stopAll = useCallback(function(){
    sessionRef.current.cancelled = true;
    if (window.speechSynthesis) {
      try { window.speechSynthesis.cancel(); } catch(e){}
    }
    setSpeaking(false);
  }, []);

  // Stop on slide change
  useEffect(function(){
    stopAll();
    setInstallModal(null);
    setShowSteps(false);
  }, [currentSlide, stopAll]);

  var speakLocal = useCallback(function(text, speechLang, voice, session, onSilentFail){
    var synth = window.speechSynthesis;
    var chunks = chunkText(text, 200);
    var i = 0;
    var anySpoke = false;

    function next(){
      if (session.cancelled) return;
      if (i >= chunks.length) {
        setSpeaking(false);
        return;
      }
      var u = new SpeechSynthesisUtterance(chunks[i]);
      u.lang = speechLang;
      u.voice = voice;
      u.rate = 0.95;
      u.pitch = 1;
      u.volume = 1;
      u.onstart = function(){ anySpoke = true; };
      u.onend = function(){ i++; next(); };
      u.onerror = function(){
        if (!anySpoke && onSilentFail) {
          session.cancelled = true;
          onSilentFail();
          return;
        }
        i++; next();
      };
      synth.speak(u);

      if (i === 0) {
        setTimeout(function(){
          if (session.cancelled) return;
          if (!anySpoke && onSilentFail) {
            try { synth.cancel(); } catch(e){}
            session.cancelled = true;
            onSilentFail();
          }
        }, 2500);
      }
    }
    next();
  }, []);

  var openInstallModal = useCallback(function(langCode){
    var langName = LANG_NAMES[langCode] || langCode.toUpperCase();
    setInstallModal({ lang: langCode, langName: langName, os: detectOS() });
    setShowSteps(false);
    setSpeaking(false);
  }, []);

  var handleToggle = useCallback(async function(){
    if (speaking) { stopAll(); return; }

    var text = getSlideText();
    if (!text) return;

    var langCode = getPageLang();
    var speechLang = LANG_MAP[langCode] || 'en-US';

    var session = { cancelled: false };
    sessionRef.current = session;
    setSpeaking(true);

    var voices = window.speechSynthesis ? await loadVoices(1500) : [];
    if (session.cancelled) return;

    var voice = findExactVoice(voices, langCode);

    if (!voice) {
      // No installed voice for this language → show install modal.
      session.cancelled = true;
      openInstallModal(langCode);
      return;
    }

    // Voice exists — try local TTS. If it silently fails, show the install modal
    // (this means the voice is enumerated but Windows can't actually render it).
    try { window.speechSynthesis.cancel(); } catch(e){}
    setTimeout(function(){
      if (session.cancelled) return;
      speakLocal(text, speechLang, voice, session, function(){
        openInstallModal(langCode);
      });
    }, 60);
  }, [speaking, stopAll, speakLocal, openInstallModal]);

  var closeModal = useCallback(function(){
    setInstallModal(null);
    setShowSteps(false);
  }, []);

  if (!supported) return null;

  return (
    <React.Fragment>
      <button
        className={'tts-btn' + (speaking ? ' tts-btn--active' : '')}
        onClick={handleToggle}
        title={speaking ? 'Stop narration' : 'Listen to this slide'}
      >
        {speaking ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1"/>
            <rect x="14" y="4" width="4" height="16" rx="1"/>
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
            <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
          </svg>
        )}
        <span className="tts-label">{speaking ? 'Stop' : 'Listen'}</span>
      </button>

      {installModal ? (
        <div className="tts-modal-backdrop notranslate" onClick={closeModal}>
          <div className="tts-modal" onClick={function(e){ e.stopPropagation(); }}>
            <button className="tts-modal-close" onClick={closeModal} title="Close">×</button>

            <div className="tts-modal-icon">🔊</div>
            <h3 className="tts-modal-title">Voice Not Installed</h3>
            <p className="tts-modal-text">
              The <strong>{installModal.langName}</strong> voice is not installed on your device.
              Install it for free from your system settings — it's a one-time download (~50–200 MB).
            </p>

            {!showSteps ? (
              <button className="tts-modal-btn" onClick={function(){ setShowSteps(true); }}>
                Install Now — Show Steps
              </button>
            ) : (
              <div className="tts-modal-steps">
                <div className="tts-modal-os">
                  {installModal.os === 'windows' && 'Windows steps:'}
                  {installModal.os === 'mac' && 'macOS steps:'}
                  {installModal.os === 'android' && 'Android steps:'}
                  {installModal.os === 'ios' && 'iOS steps:'}
                  {installModal.os === 'linux' && 'Linux steps:'}
                  {installModal.os === 'other' && 'Steps:'}
                </div>
                <ol className="tts-modal-list">
                  {getInstallSteps(installModal.os, installModal.langName).map(function(step, i){
                    return <li key={i}>{step}</li>;
                  })}
                </ol>
                <button className="tts-modal-btn tts-modal-btn--ghost" onClick={closeModal}>
                  Got it
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </React.Fragment>
  );
}
